import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { MARKET } from "@/lib/format";
import { COMPANY, sized } from "@/lib/content";
import { Reveal } from "@/components/public/Reveal";
import { Btn, SectionHead, Photo, Facts, Rows, Img, CTA, DimLine, Eyebrow } from "@/components/public/ui";
import { Skeleton } from "@/components/ui/skeleton";
import { ProjectTile } from "@/components/public/ProjectCard";

const MARKET_SLUG = { industri: "industri", samferdsel: "samferdsel", bygg_naering: "bygg-naering", service_bustad: "service-bustad" };

export default function ProjectCase() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setP(null);
    setErr(false);
    api.get(`/public/projects/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <div className="container-public pb-32 pt-20">
        <h1 className="t-h2">Fann ikkje prosjektet</h1>
        <Link to="/prosjekt" className="link-arrow mt-6 inline-flex items-center gap-2 text-[15px] font-medium"><ArrowLeft className="h-4 w-4" /> Alle prosjekt</Link>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="container-public pt-10 sm:pt-14 lg:pt-20">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="mt-8 h-16 w-3/4" />
        <Skeleton className="mt-10 aspect-[21/9] w-full rounded-pub" />
      </div>
    );
  }

  const nextP = p.related?.[0];
  const rest = p.related?.slice(1) || [];

  return (
    <article data-testid="public-project-case">
      <section className="container-public pt-10 sm:pt-14 lg:pt-20">
        <Link to="/prosjekt" className="inline-flex items-center gap-2 text-[14px] font-medium text-muted-foreground hover:text-ink" data-testid="public-case-back-link"><ArrowLeft className="h-4 w-4" /> Alle prosjekt</Link>
        <p className="t-mono mt-8">{MARKET[p.market]} · {p.location} · {p.year}</p>
        <h1 className="t-h1 mt-4 max-w-[18ch] text-ink">{p.title}</h1>
        <p className="t-lead mt-6 max-w-2xl">{p.subtitle}</p>
        <DimLine className="mt-10" />
        <dl className="grid grid-cols-2 gap-6 pt-6 text-[15px] md:grid-cols-4" data-testid="public-case-facts">
          {[["Kunde", p.client], ["Stad", p.location], ["År", p.year], ["Fagområde", <Link key="m" to={`/${MARKET_SLUG[p.market]}`} className="u-link">{MARKET[p.market]}</Link>]].map(([l, v]) => (
            <div key={l}>
              <dt className="text-[13px] text-muted-foreground">{l}</dt>
              <dd className="mt-1 font-medium text-ink">{v}</dd>
            </div>
          ))}
        </dl>
        <Photo className="mt-10" src={sized(p.hero_image, 2400)} alt="" ratio="aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]" sizes="100vw" priority />
      </section>

      <section className="container-public pt-20 sm:pt-28" data-testid="public-case-summary">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <p className="font-display text-[1.5rem] font-semibold leading-[1.3] tracking-[-0.02em] text-ink sm:text-[1.85rem]">{p.summary}</p>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-5">
            <Eyebrow>Omfang</Eyebrow>
            <Rows className="mt-3">
              {p.scope.map((s) => (<li key={s} className="py-3 text-[15px]">{s}</li>))}
            </Rows>
          </Reveal>
        </div>
      </section>

      {p.images?.length > 0 && (
        <section className="container-public pt-20 sm:pt-28" data-testid="public-case-gallery">
          <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-6">
            {p.images.map((src, i) => (
              <Photo
                key={src + i}
                src={sized(src, i < 2 ? 1400 : 900)}
                alt=""
                flag={false}
                ratio={i === 0 ? "aspect-[16/10]" : i === 1 ? "aspect-[16/10] md:aspect-auto md:h-full" : "aspect-[4/3]"}
                className={i === 0 ? "col-span-2 md:col-span-4" : i === 1 ? "col-span-2 md:col-span-2" : "md:col-span-2"}
              />
            ))}
          </div>
        </section>
      )}

      {p.key_numbers?.length > 0 && (
        <section className="container-public pt-20 sm:pt-28" data-testid="public-case-key-numbers">
          <Facts items={p.key_numbers.map((k) => [k.value, k.label])} cols="grid-cols-2 lg:grid-cols-4" big />
        </section>
      )}

      <section className="container-public pt-20 sm:pt-28" data-testid="public-case-deliverables">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <h2 className="t-h3 text-ink">Leveranse</h2>
            <Rows className="mt-5">
              {p.deliverables.map((d) => (<li key={d} className="py-3.5 text-[15px] sm:text-base">{d}</li>))}
            </Rows>
          </div>
          <div className="lg:col-span-5" data-testid="public-case-contact">
            <h2 className="t-h3 text-ink">Vil du vite meir?</h2>
            <div className="mt-5 rounded-pub bg-paper p-6">
              <p className="text-[17px] font-semibold text-ink">{COMPANY.legal}</p>
              <p className="text-[15px] text-muted-foreground">{COMPANY.hq.address}</p>
              <div className="mt-4 flex flex-col gap-1 text-[15px]">
                <a href={COMPANY.phoneHref} className="u-link tabular w-fit font-medium text-ink">{COMPANY.phone}</a>
                <a href={`mailto:${COMPANY.email}`} className="u-link w-fit font-medium text-ink">{COMPANY.email}</a>
              </div>
              <Btn to="/kontakt" variant="dark" size="md" arrow className="mt-6" data-testid="public-case-contact-button">Ta kontakt</Btn>
            </div>
          </div>
        </div>
      </section>

      {rest.length > 0 && (
        <section className="container-public pt-20 sm:pt-28" data-testid="public-case-related">
          <SectionHead eyebrow="Prosjekt" title="Fleire prosjekt" />
          <div className="mt-10 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((r, i) => (<Reveal key={r.slug} delay={(i % 3) * 0.05}><ProjectTile project={r} /></Reveal>))}
          </div>
        </section>
      )}

      {nextP && (
        <section className="container-public pt-20 sm:pt-28">
          <Link to={`/prosjekt/${nextP.slug}`} className="group grid overflow-hidden rounded-3xl bg-ink text-white sm:grid-cols-12" data-testid="public-case-next-link">
            <div className="aspect-[16/10] overflow-hidden sm:col-span-5 sm:aspect-auto">
              <Img src={sized(nextP.hero_image, 1400)} alt="" loading="lazy" className="tile-img img-premium h-full w-full object-cover" />
            </div>
            <div className="flex flex-col justify-between gap-6 p-6 sm:col-span-7 sm:p-10">
              <div>
                <p className="text-[13px] font-medium text-white/60">Neste prosjekt</p>
                <p className="t-h2 mt-2 text-white">{nextP.title}</p>
                <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-white/70">{nextP.subtitle}</p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[15px] font-medium text-white/85 group-hover:text-white">Sjå prosjektet <ArrowUpRight className="h-4 w-4" /></span>
            </div>
          </Link>
        </section>
      )}

      <div className="pt-20 sm:pt-28">
        <CTA />
      </div>
    </article>
  );
}
